type DeferredAction = () => void;

const activeOperations = new Set<symbol>();
const deferredActions = new Set<DeferredAction>();

const runDeferredActionSafely = (action: DeferredAction) => {
  try {
    action();
  } catch (error) {
    // App lifecycle work must never change the outcome of the operation that
    // just released the gate. Keep flushing any other deferred work.
    console.error("Deferred critical-operation action failed", error);
  }
};

const flushDeferredActions = () => {
  if (activeOperations.size > 0) return;

  const actions = Array.from(deferredActions);
  deferredActions.clear();

  actions.forEach((action) => {
    if (activeOperations.size > 0) {
      deferredActions.add(action);
    } else {
      runDeferredActionSafely(action);
    }
  });
};

/**
 * Hold app-controlled reloads while an in-memory mutation identity is needed.
 * The returned release function is idempotent and stores no operation data.
 */
export const beginCriticalOperation = (): (() => void) => {
  const token = Symbol("critical-operation");
  activeOperations.add(token);
  let released = false;

  return () => {
    if (released) return;
    released = true;
    activeOperations.delete(token);
    flushDeferredActions();
  };
};

export const isCriticalOperationActive = () => activeOperations.size > 0;

/** Run once when every critical-operation token has been released. */
export const runWhenNoCriticalOperation = (action: DeferredAction): (() => void) => {
  if (!isCriticalOperationActive()) {
    runDeferredActionSafely(action);
    return () => undefined;
  }

  let cancelled = false;
  const deferredAction = () => {
    if (!cancelled) action();
  };
  deferredActions.add(deferredAction);

  return () => {
    cancelled = true;
    deferredActions.delete(deferredAction);
  };
};
